import { notificationsApi } from "@/api/NotificationsApi";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i)
    outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

const subscribeToPushNotifications = async () => {
  const registration = await navigator.serviceWorker.ready;
  try {
    const vapidPublicKey = await notificationsApi.getVapidPublicKey();
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        vapidPublicKey,
      ) as BufferSource,
    });
    await notificationsApi.addPushSubscription({
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      keys: subscription.toJSON().keys ?? {},
    });
  } catch (error) {
    console.error("[Push] subscribeToPushNotifications failed", error);
  }
};

export const unsubscribeFromPushNotifications = async ({
  localOnly = false,
}: { localOnly?: boolean } = {}) => {
  try {
    if (!("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      if (!localOnly) {
        try {
          await notificationsApi.removePushSubscription(subscription.endpoint);
        } catch (error) {
          console.error(
            "[Push] Failed to remove subscription from server",
            error,
          );
        }
      }
      await subscription.unsubscribe();
    }
  } catch (error) {
    console.error("[Push] Failed to unsubscribe from push", error);
  }
};

export const usePushNotificationsSetup = () => {
  const queryClient = useQueryClient();
  const alreadyRequestedPermission = useRef(false);

  const ensurePushSubscription = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    if (
      Notification.permission === "default" &&
      !alreadyRequestedPermission.current
    ) {
      alreadyRequestedPermission.current = true;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;
    }
    if (Notification.permission !== "granted") return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      await subscribeToPushNotifications();
    } else if (
      subscription.expirationTime !== null &&
      subscription.expirationTime < Date.now()
    ) {
      await subscription.unsubscribe();
      await subscribeToPushNotifications();
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    ensurePushSubscription();
  }, [ensurePushSubscription]);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }

    const onMessage = async (event: MessageEvent) => {
      if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGE") {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscribeToPushNotifications();
        }
      } else if (event.data?.type === "NEW_NOTIFICATION") {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [queryClient]);
};
